import { cn } from '@/lib/utils';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: { bg: 'bg-slate-900', text: 'text-white', textMuted: 'text-slate-400', card: 'bg-slate-800', border: 'border-slate-700' },
  theatre_red: { bg: 'bg-[#1a0c0e] tx-velvet', text: 'text-[#f8f0e6]', textMuted: 'text-[#bfa39a]', card: 'bg-[#120709] tx-velvet-alt', border: 'border-[#3b2125]' },
  ivory_gold: { bg: 'bg-[#faf7f1]', text: 'text-[#1c1710]', textMuted: 'text-[#8d7f68]', card: 'bg-[#f1ebdd]', border: 'border-[#e2d8c3]' },
  light_modern: { bg: 'bg-white', text: 'text-slate-900', textMuted: 'text-slate-500', card: 'bg-slate-50', border: 'border-slate-200' },
  colorful_kids: { bg: 'bg-amber-50', text: 'text-purple-900', textMuted: 'text-purple-600', card: 'bg-white', border: 'border-pink-200' },
  elegant_classic: { bg: 'bg-stone-900', text: 'text-stone-50', textMuted: 'text-stone-400', card: 'bg-stone-800', border: 'border-stone-700' },
  bold_artist: { bg: 'bg-zinc-900', text: 'text-white', textMuted: 'text-zinc-400', card: 'bg-zinc-800', border: 'border-zinc-700' },
};

export const TimetableSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const groups = (data?.groups || []).filter((g) => g.title || g.schedule?.length);
  const prices = data?.prices || [];

  if (groups.length === 0 && prices.length === 0) return null;

  return (
    <section id="timetable" className={cn("py-24 px-4", t.card)}>
      <div className="max-w-6xl mx-auto">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>Weekly Schedule</span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>{data.title || 'Timetable'}</h2>
        </AnimatedSection>

        <div className="space-y-8">
          {prices.length > 0 && (
            <AnimatedSection animation="fadeUp" className={cn("rounded-3xl border p-8 md:p-10", t.bg, t.border)} data-testid="timetable-pricing">
              <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                <div className={cn("grid gap-6 flex-1", prices.length >= 4 ? "grid-cols-2 md:grid-cols-4" : `grid-cols-${Math.min(prices.length, 3)}`)}>
                  {prices.map((p, i) => (
                    <div key={i} className="text-center">
                      <p className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: primaryColor }}>{p.price}</p>
                      <p className={cn("text-xs md:text-sm mt-1.5 font-medium", t.textMuted)}>{p.label}</p>
                    </div>
                  ))}
                </div>
                {(data.badge || data.note) && (
                  <div className="lg:max-w-xs lg:border-l lg:pl-8 space-y-3" style={{ borderColor: primaryColor + '30' }}>
                    {data.badge && (
                      <span className="inline-block text-xs font-bold uppercase tracking-wider text-white px-3.5 py-1.5 rounded-full" style={{ backgroundColor: primaryColor }}>
                        {data.badge}
                      </span>
                    )}
                    {data.note && <p className={cn("text-xs leading-relaxed", t.textMuted)}>{data.note}</p>}
                  </div>
                )}
              </div>
            </AnimatedSection>
          )}

          <div className={cn("grid gap-6 items-start", groups.length >= 3 ? "lg:grid-cols-3" : groups.length === 2 ? "lg:grid-cols-2" : "max-w-2xl mx-auto")}>
            {groups.map((group, idx) => (
              <AnimatedSection key={group.id || idx} animation="fadeUp" delay={idx * 120} className={cn("rounded-3xl border overflow-hidden", t.bg, t.border)}>
                <div className="p-6 pb-5" style={{ backgroundColor: primaryColor + '12' }}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className={cn("text-xl font-bold", t.text)}>{group.title}</h3>
                    {group.price && (
                      <span className="text-xs font-bold whitespace-nowrap px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: primaryColor }}>{group.price}</span>
                    )}
                  </div>
                  {group.description && <p className={cn("text-xs mt-2 leading-relaxed", t.textMuted)}>{group.description}</p>}
                </div>
                <div className="p-6 pt-4 space-y-5">
                  {(group.schedule || []).map((dayGroup, gi) => (
                    <div key={gi}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryColor }} />
                        <p className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: primaryColor }}>{dayGroup.day}</p>
                        <div className={cn("flex-1 border-t", t.border)} />
                      </div>
                      <div className="space-y-2">
                        {(dayGroup.entries || []).map((entry, ei) => (
                          <div key={ei} className="flex items-center justify-between gap-3">
                            <p className={cn("text-sm leading-snug", t.text)}>{entry.name}</p>
                            {entry.time && (
                              <span className="text-xs font-semibold whitespace-nowrap px-2.5 py-1 rounded-full" style={{ backgroundColor: primaryColor + '15', color: primaryColor }}>
                                {entry.time}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
